import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import gameApi from '../../services/GameApiService';
import {
  saveSession,
  type SavedSession,
} from '../../utils/sessionPersistence';
import { saveDeviceToken } from '../../utils/deviceSession';

interface StartAdventureFormProps {
  adventureId: string;
  adventureTitle: string;
}

/**
 * Botão "Começar a jogar" — arranque anónimo.
 *
 * O jogador entra na aventura sem escrever email. O backend cria um
 * `playerState` com `email: null` (B2) e devolve um token JWT (365 dias)
 * que guardamos no cookie/localStorage do device (F1). O email só é
 * pedido no fim do episódio 2 (F4), como ponte para sincronização
 * cross-device.
 *
 * O caminho cross-device ("Já joguei antes noutro dispositivo") vive no
 * catálogo (F5) e usa `startGame` (email → magic link → `/check-email`).
 */
export default function StartAdventureForm({
  adventureId,
}: StartAdventureFormProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  async function handleStart() {
    setIsLoading(true);
    try {
      const result = await gameApi.startAnonymous(adventureId);

      // Grava o token no cookie (fonte durável) e a sessão de display.
      saveDeviceToken(result.token);
      const sessionData: Omit<SavedSession, 'savedAt'> = {
        token: result.token,
        adventureTitle: result.adventureTitle,
        adventureId: result.adventureId,
        currentEpisode: result.currentEpisode,
        totalEpisodes: result.totalEpisodes,
        lastPage: 'play',
        playerStatus: result.playerStatus,
      };
      saveSession(sessionData);

      navigate(`/play/${result.token}`);
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  }

  return (
    <div className="w-full max-w-md">
      <button
        type="button"
        onClick={handleStart}
        disabled={isLoading}
        className="w-full py-4 px-6 text-lg font-semibold text-white bg-gradient-to-r from-primary to-accent rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            A começar...
          </span>
        ) : (
          'Começar a jogar'
        )}
      </button>
      <p className="mt-3 text-sm text-gray-500 text-center">
        Sem registo. Começa já e pede-se o email só mais tarde, para
        sincronizar entre dispositivos.
      </p>
    </div>
  );
}
